/**
 * Org-level GET /access in a compact transport shape (App Server PRs 7075 and access-refetch 3b).
 *
 * The full response repeats every direct permission twice: once in `people` and again in
 * `devices`. 15,783 rows each for The Catalina. `?shape=compact` sends the device copy as a
 * permissionID in `devicesFromPeople`; this rebuilds it from the `people` row (dropping the
 * people-only `entityTypeID`), so the store holds the same four arrays it always has and no
 * selector or screen needs to know the shape exists.
 *
 * `?shape=columnar` is compact with each list sent as `{ columns, rows }`: the field names once and
 * each row a tuple in column order, about half the bytes of compact. Every field is kept; decoding
 * rebuilds the same objects, keys in the same order.
 *
 * The server references a device row only when it equals its people row on every field, so the
 * rebuild is exact. A full-shape response (a server without compact) passes through untouched.
 */
export const ACCESS_COLUMNAR_QUERY = { shape: 'columnar' };
export const ACCESS_COMPACT_QUERY = { shape: 'compact' };

/** A columnar list back to row objects; an array (already rows) passes through. */
export const decodeColumns = (list) => {
	if (!list) return [];
	if (Array.isArray(list)) return list;
	const { columns = [], rows = [] } = list;
	return rows.map((tuple) => {
		const row = {};
		for (let i = 0; i < columns.length; i += 1) row[columns[i]] = tuple[i];
		return row;
	});
};

const decodeColumnar = ({ shape, people, teams, devices, groups, ...rest }) => ({
	...rest,
	shape: 'compact',
	people: decodeColumns(people),
	teams: decodeColumns(teams),
	devices: decodeColumns(devices),
	groups: decodeColumns(groups)
});

export const expandCompactAccess = (input) => {
	const data = input?.shape === 'columnar' ? decodeColumnar(input) : input;
	if (!data || data.shape !== 'compact') return data;
	const { shape, devicesFromPeople = [], ...lists } = data;
	const people = lists.people || [];
	const peopleByID = new Map(people.map((p) => [p.permissionID, p]));
	const rebuilt = [];
	for (const permissionID of devicesFromPeople) {
		const person = peopleByID.get(permissionID);
		if (!person) continue;
		const { entityTypeID, ...row } = person;
		rebuilt.push(row);
	}
	return { ...lists, people, devices: [...rebuilt, ...(lists.devices || [])] };
};

// Most compact first. A server that predates a shape answers it with a 400 (unknown value), so each
// 400 steps down one shape and a rollback of either side stays safe.
const SHAPES = [ACCESS_COLUMNAR_QUERY, ACCESS_COMPACT_QUERY, {}];

/** GET /access for the org in the most compact shape the server supports, expanded to the full lists. */
export const fetchOrgAccess = async (SuperFetch, query) => {
	let response;
	for (const shape of SHAPES) {
		response = await SuperFetch.get('/access', {}, { ...query, ...shape });
		if (response.statusCode !== 400) break;
	}
	return response.statusCode === 200 ? { ...response, data: expandCompactAccess(response.data) } : response;
};
